import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Input } from '../components/ui/input.tsx';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { EnvironmentSelector } from '../features/feature-flags/components/EnvironmentSelector.tsx';
import { FlagDetailSheet } from '../features/feature-flags/components/FlagDetailSheet.tsx';
import { useConfigVersion, useFlags } from '../features/feature-flags/hooks/useFlags.ts';
import type { FlagDefinition } from '../features/feature-flags/api/types.ts';
import { Search } from 'lucide-react';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';

export function FeatureFlagsPage() {
  const [environment, setEnvironment] = useState('development');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFlag, setSelectedFlag] = useState<FlagDefinition | null>(null);
  const tableState = useTableUrlState();

  const { flags, isLoading, error } = useFlags(environment, searchQuery || undefined);
  const { version } = useConfigVersion(environment);

  const filteredFlags = useMemo(() => {
    if (!searchQuery.trim()) return flags;

    const query = searchQuery.toLowerCase();
    return flags.filter((flag) =>
      flag.key.toLowerCase().includes(query) ||
      flag.description.toLowerCase().includes(query)
    );
  }, [flags, searchQuery]);

  const columns: ColumnDef<FlagDefinition>[] = [
    {
      accessorKey: 'key',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Flag Key' />,
      cell: ({ row }) => <span className='font-mono text-sm'>{row.getValue('key')}</span>,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Description' />,
      cell: ({ row }) => (
        <span className='text-sm truncate max-w-[300px] block'>
          {row.getValue('description')}
        </span>
      ),
    },
    {
      accessorKey: 'enabled',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => {
        const enabled = row.getValue('enabled') as boolean;
        return enabled
          ? <Badge variant='default' className='bg-green-600 hover:bg-green-600'>Enabled</Badge>
          : <Badge variant='secondary'>Disabled</Badge>;
      },
    },
    {
      id: 'rules',
      accessorFn: (row) => row.rules.length,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Rules' />,
      cell: ({ row }) => (
        <span className='text-sm text-muted-foreground'>
          {row.getValue('rules')}
        </span>
      ),
    },
  ];

  if (error) {
    return (
      <div className='container mx-auto py-6'>
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Feature Flags</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='flex items-center justify-center py-8' role='status'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>Feature Flags</h1>
          <p className='text-muted-foreground mt-2'>
            Manage FlowControl feature flags across environments
          </p>
        </div>
        <EnvironmentSelector value={environment} onChange={setEnvironment} />
      </div>

      {version && (
        <div className='text-sm text-muted-foreground mb-4'>
          Config version: <span className='font-mono'>{version.version}</span>
          {' | Last updated: '}
          {new Date(version.lastUpdated).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Flags</CardTitle>
          <CardDescription>
            {environment.charAt(0).toUpperCase() + environment.slice(1)} environment
            {' \u2014 '}
            {filteredFlags.length} flag{filteredFlags.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='relative flex-1'>
            <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search flags by key or description...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-8'
            />
          </div>

          <div className='text-sm text-muted-foreground'>
            Showing {filteredFlags.length} flag{filteredFlags.length !== 1 ? 's' : ''}
          </div>

          <DataTable
            columns={columns}
            data={filteredFlags}
            onRowClick={(flag) => setSelectedFlag(flag)}
            sorting={tableState.sorting}
            onSortingChange={tableState.onSortingChange}
            pagination={tableState.pagination}
            onPaginationChange={tableState.onPaginationChange}
          />
        </CardContent>
      </Card>

      <FlagDetailSheet
        flag={selectedFlag}
        environment={environment}
        open={selectedFlag !== null}
        onOpenChange={(open) => !open && setSelectedFlag(null)}
      />
    </div>
  );
}
